import React, { useState, useEffect } from 'react';
import { ReceiptStatus, Account } from '../../../../apiServices/types';
import { receiptService } from '../../../../apiServices/receiptService';
import { accountService } from '../../../../apiServices';
import { useNotification } from '../../../../contexts/NotificationContext';
import { toUTCDateString } from '../../../../utils';
import { GlassSelect } from '../../../UI';

interface ShoppingListHeaderProps {
  listName: string;
  status: 'planning' | 'shopping';
  priceMode: boolean;
  receiptStatus?: unknown;
  receiptId?: string;
  receiptDate?: string;
  accountId?: string;
  accountName?: string;
  onTogglePriceMode?: () => void;
  onDateUpdate?: () => void;
  onAccountUpdate?: () => void;
  isDeleted?: boolean;
}

const ShoppingListHeader: React.FC<ShoppingListHeaderProps> = ({
  listName,
  status,
  priceMode,
  receiptStatus,
  receiptId,
  receiptDate,
  accountId,
  accountName,
  onDateUpdate,
  onAccountUpdate,
  isDeleted = false
}) => {
  const { showNotification } = useNotification();
  const [isEditingDate, setIsEditingDate] = useState(false);
  const [dateValue, setDateValue] = useState(receiptDate || '');
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(listName);
  const [isEditingAccount, setIsEditingAccount] = useState(false);
  const [accountValue, setAccountValue] = useState<string>(accountId || '');
  const [accounts, setAccounts] = useState<Account[]>([]);

  const getReceiptStatusText = (status: unknown): string => {
    if (typeof status === 'string') {
      return status === 'Completed' ? 'Завершено' : 'В процесі';
    }
    return Number(status) === ReceiptStatus.Completed ? 'Завершено' : 'В процесі';
  };

  const getReceiptStatusClass = (status: unknown): string => {
    if (typeof status === 'string') {
      return status === 'Completed' ? 'status-completed' : 'status-in-progress';
    }
    return Number(status) === ReceiptStatus.Completed ? 'status-completed' : 'status-in-progress';
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('uk-UA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleDateSave = async () => {
    if (!receiptId || !dateValue) return;

    try {
      const dateWithTime = toUTCDateString(dateValue);

      await receiptService.update(receiptId, { name: listName, date: dateWithTime });
      setIsEditingDate(false);
      showNotification('Дату оновлено', 'success');
      onDateUpdate?.();
    } catch (error) {
      showNotification('Помилка оновлення дати', 'error');
    }
  };

  const handleDateCancel = () => {
    setDateValue(receiptDate || '');
    setIsEditingDate(false);
  };

  const handleDateKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleDateSave();
    } else if (e.key === 'Escape') {
      handleDateCancel();
    }
  };

  useEffect(() => {
    setNameValue(listName);
  }, [listName]);

  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const data = await accountService.getAll();
        setAccounts(data);
      } catch (error) {
        showNotification('Помилка завантаження рахунків', 'error');
      }
    };
    loadAccounts();
  }, [showNotification]);

  useEffect(() => {
    if (!isEditingAccount) {
      setAccountValue(accountId || '');
    }
  }, [accountId, isEditingAccount]);

  const handleAccountSave = async () => {
    if (!receiptId) return;

    try {
      await receiptService.update(receiptId, { 
        name: listName, 
        date: receiptDate,
        accountId: accountValue || undefined
      });
      setIsEditingAccount(false);
      showNotification('Рахунок оновлено', 'success');
      onDateUpdate?.();
      onAccountUpdate?.();
    } catch (error) {
      showNotification('Помилка оновлення рахунку', 'error');
    }
  };

  const handleAccountCancel = () => {
    setAccountValue(accountId || '');
    setIsEditingAccount(false);
  };

  const getAccountDisplayName = (): string => {
    if (!accountId) return 'Не вказано';
    const account = accounts.find(a => a.id === accountId);
    return account ? account.name : (accountName || 'Не вказано');
  };

  const handleNameSave = async () => {
    if (!receiptId || !nameValue.trim()) return;

    try {
      await receiptService.update(receiptId, { name: nameValue.trim(), date: receiptDate });
      setIsEditingName(false);
      showNotification('Назву оновлено', 'success');
      onDateUpdate?.();
    } catch (error) {
      showNotification('Помилка оновлення назви', 'error');
    }
  };

  const handleNameCancel = () => {
    setNameValue(listName);
    setIsEditingName(false);
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleNameSave();
    } else if (e.key === 'Escape') {
      handleNameCancel();
    }
  };

  return (
    <div className="shopping-header">
      <div className="shopping-header-left">
        <div className="shopping-header-content">
          {isEditingName ? (
            <div className="name-edit-controls">
              <input
                type="text"
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                onKeyDown={handleNameKeyDown}
                className="name-input"
                autoFocus
              />
              <button className="btn-icon-small btn-icon-success" onClick={handleNameSave} title="Зберегти">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </button>
              <button className="btn-icon-small btn-icon-secondary" onClick={handleNameCancel} title="Скасувати">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          ) : (
            <h2 
              className="shopping-title"
              onClick={() => !isDeleted && setIsEditingName(true)}
              title={isDeleted ? 'Чек видалено' : 'Натисніть для редагування'}
              style={{ cursor: isDeleted ? 'default' : 'pointer', opacity: isDeleted ? 0.6 : 1 }}
            >
              {listName}
            </h2>
          )}
          <div className="shopping-status-container">
            {receiptStatus !== undefined && (
              <span className={`receipt-status ${getReceiptStatusClass(receiptStatus)}`}>
                {getReceiptStatusText(receiptStatus)}
              </span>
            )}
            {isEditingAccount ? (
              <div className="account-edit-controls">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <GlassSelect
                    options={accounts.map(account => ({
                      value: account.id,
                      label: account.name
                    }))}
                    value={accountValue}
                    onChange={(value) => setAccountValue(value)}
                    placeholder="Оберіть рахунок"
                    searchable
                  />
                </div>
                <button className="btn-icon-small btn-icon-success" onClick={handleAccountSave} title="Зберегти">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </button>
                <button className="btn-icon-small btn-icon-secondary" onClick={handleAccountCancel} title="Скасувати">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
            ) : (
              <button
                className="receipt-account-display"
                onClick={() => !isDeleted && setIsEditingAccount(true)}
                title={isDeleted ? 'Чек видалено' : 'Натисніть для редагування'}
                disabled={isDeleted}
                style={{ opacity: isDeleted ? 0.6 : 1, cursor: isDeleted ? 'default' : 'pointer' }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                  <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                </svg>
                <span>{getAccountDisplayName()}</span>
              </button>
            )}
            {receiptDate && (
              <>
                {isEditingDate ? (
                  <div className="date-edit-controls">
                    <input
                      type="date"
                      value={dateValue.split('T')[0]}
                      onChange={(e) => setDateValue(e.target.value)}
                      onKeyDown={handleDateKeyDown}
                      className="date-input"
                    />
                    <button className="btn-icon-small btn-icon-success" onClick={handleDateSave} title="Зберегти">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </button>
                    <button className="btn-icon-small btn-icon-secondary" onClick={handleDateCancel} title="Скасувати">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </div>
                ) : (
                  <button
                    className="receipt-date-display"
                    onClick={() => !isDeleted && setIsEditingDate(true)}
                    title={isDeleted ? 'Чек видалено' : 'Натисніть для редагування'}
                    disabled={isDeleted}
                    style={{ opacity: isDeleted ? 0.6 : 1, cursor: isDeleted ? 'default' : 'pointer' }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                    <span>{formatDate(receiptDate)}</span>
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShoppingListHeader;
